import React from 'react';
import { School, Users, GraduationCap, Heart, BookOpen, ShieldCheck, Activity, TrendingUp, AlertTriangle, CheckCircle2, Clock, Server, RefreshCw, Tag } from 'lucide-react';
import { motion } from 'framer-motion';
import { PageLayout } from '../../shared/PageLayout';
import { StatCard } from '../../shared/StatCard';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { QualityRing, ProgressBar, containerVariants, itemVariants } from './dashboard/DashboardCharts';
import { useDashboardData } from './dashboard/useDashboardData';
export function DashboardPage({
  onNavigate
}: {
  onNavigate?: (view: string) => void;
}) {
  const {
    stats,
    contentStats,
    pipelineLoading,
    strictModeOn,
    handleRefreshPipeline,
    passCount,
    warnCount,
    failCount,
    avgScore,
    totalValidated,
    systemHealth,
    recentActivity
  } = useDashboardData();
  return <PageLayout title="Dashboard" subtitle="Overzicht van het Meester djay platform" actions={<ConnectionBadge status="live" data-id="element-3947" />} data-id="element-3946">
      {/* Section 1: Platform Overview */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6" data-id="element-3948">
        <motion.div variants={itemVariants} data-id="element-3949">
          <StatCard label="Leerlingen" value={stats.totalLearners} icon={<Users className="w-4 h-4 text-blue-500" data-id="element-3951" />} data-id="element-3950" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-3952">
          <StatCard label="Docenten" value={stats.totalTeachers} icon={<GraduationCap className="w-4 h-4 text-indigo-500" data-id="element-3954" />} data-id="element-3953" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-3955">
          <StatCard label="Ouders" value={stats.totalParents} icon={<Heart className="w-4 h-4 text-rose-500" data-id="element-3957" />} data-id="element-3956" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-3958">
          <StatCard label="Scholen" value={stats.totalSchools} icon={<School className="w-4 h-4 text-purple-500" data-id="element-3960" />} data-id="element-3959" />
        </motion.div>
      </motion.div>

      {/* Section 2: Content & Quality */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5 mb-6" data-id="element-3961">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-3962">
          Content & Kwaliteit
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-3963">
          {/* Left: Content Stats */}
          <div className="space-y-4" data-id="element-3964">
            <div className="flex items-center justify-between" data-id="element-3965">
              <div className="flex items-center gap-2" data-id="element-3966">
                <BookOpen className="w-4 h-4 text-blue-500" data-id="element-3967" />
                <span className="text-sm font-medium text-gray-700" data-id="element-3968">
                  Vragenbank
                </span>
              </div>
              <span className="text-lg font-bold text-gray-900" data-id="element-3969">
                {contentStats.totalQuestions.toLocaleString('nl')}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-3970">
              <div className="flex items-center gap-2" data-id="element-3971">
                <Tag className="w-4 h-4 text-purple-500" data-id="element-3972" />
                <span className="text-sm font-medium text-gray-700" data-id="element-3973">
                  Families
                </span>
              </div>
              <span className="text-sm text-gray-600" data-id="element-3974">
                {contentStats.totalFamilies} / {contentStats.taxonomyFamilies}
              </span>
            </div>
            <ProgressBar value={contentStats.coveragePct} color={contentStats.coveragePct >= 80 ? 'bg-emerald-500' : contentStats.coveragePct >= 50 ? 'bg-amber-500' : 'bg-red-500'} label="Seed dekking" data-id="element-3975" />
            <ProgressBar value={contentStats.visualCoveragePct} color={contentStats.visualCoveragePct >= 80 ? 'bg-emerald-500' : contentStats.visualCoveragePct >= 50 ? 'bg-amber-500' : 'bg-red-500'} label={`Visuele componenten (${contentStats.usedVisualsCount}/${contentStats.registeredVisuals})`} data-id="element-3976" />
          </div>

          {/* Right: Quality & Validation */}
          <div className="space-y-4" data-id="element-3977">
            <div className="flex items-center justify-between" data-id="element-3978">
              <div className="flex items-center gap-3" data-id="element-3979">
                {avgScore !== null ? <QualityRing score={avgScore} data-id="element-3980" /> : <div className="w-12 h-12 rounded-full bg-gray-100 animate-pulse" data-id="element-3981" />}
                <div data-id="element-3982">
                  <div className="text-sm font-medium text-gray-700" data-id="element-3983">
                    Kwaliteitsscore
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-3984">
                    alle {contentStats.totalQuestions.toLocaleString('nl')}{' '}
                    vragen
                  </div>
                </div>
              </div>
              <button onClick={handleRefreshPipeline} disabled={pipelineLoading} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40" title="Validatie vernieuwen" data-id="element-3985">
                <RefreshCw className={`w-4 h-4 ${pipelineLoading ? 'animate-spin' : ''}`} data-id="element-3986" />
              </button>
            </div>

            {pipelineLoading ? <div className="flex gap-4" data-id="element-3987">
                {['Geslaagd', 'Warnings', 'Fouten'].map(l => <div key={l} className="flex-1 bg-gray-50 rounded-lg p-2 animate-pulse" data-id="element-3988">
                    <div className="h-5 bg-gray-200 rounded mb-1" data-id="element-3989" />
                    <div className="h-3 bg-gray-100 rounded w-2/3" data-id="element-3990" />
                  </div>)}
              </div> : passCount !== null ? <>
                <div className="flex gap-3" data-id="element-3991">
                  <div className="flex-1 bg-emerald-50 rounded-lg p-2.5 text-center" data-id="element-3992">
                    <div className="text-lg font-bold text-emerald-700" data-id="element-3993">
                      {passCount}
                    </div>
                    <div className="text-xs text-emerald-600" data-id="element-3994">Geslaagd</div>
                  </div>
                  <div className="flex-1 bg-amber-50 rounded-lg p-2.5 text-center" data-id="element-3995">
                    <div className="text-lg font-bold text-amber-700" data-id="element-3996">
                      {warnCount}
                    </div>
                    <div className="text-xs text-amber-600" data-id="element-3997">Warnings</div>
                  </div>
                  <div className="flex-1 bg-red-50 rounded-lg p-2.5 text-center" data-id="element-3998">
                    <div className="text-lg font-bold text-red-700" data-id="element-3999">
                      {failCount}
                    </div>
                    <div className="text-xs text-red-600" data-id="element-4000">Fouten</div>
                  </div>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex" data-id="element-4001">
                  <div className="h-full bg-emerald-500" style={{
                width: `${totalValidated > 0 ? (passCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-4002" />
                  <div className="h-full bg-amber-400" style={{
                width: `${totalValidated > 0 ? (warnCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-4003" />
                  <div className="h-full bg-red-500" style={{
                width: `${totalValidated > 0 ? (failCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-4004" />
                </div>
              </> : <div className="text-sm text-gray-400 text-center py-4" data-id="element-4005">
                Validatie wordt geladen...
              </div>}
          </div>
        </div>
      </motion.div>

      {/* Section 3: Activity & System Health */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6" data-id="element-4006">
        {/* Recent Activity */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4007">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4008">
            <Clock className="w-4 h-4" data-id="element-4009" /> Recente Activiteit
          </h3>
          <div className="space-y-3" data-id="element-4010">
            {recentActivity.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-4011">
                <div className={`mt-0.5 flex-shrink-0 ${item.type === 'success' ? 'text-emerald-500' : item.type === 'warning' ? 'text-amber-500' : 'text-blue-500'}`} data-id="element-4012">
                  {item.type === 'success' ? <CheckCircle2 className="w-4 h-4" data-id="element-4013" /> : item.type === 'warning' ? <AlertTriangle className="w-4 h-4" data-id="element-4014" /> : <Activity className="w-4 h-4" data-id="element-4015" />}
                </div>
                <div className="min-w-0 flex-1" data-id="element-4016">
                  <p className="text-sm text-gray-700 leading-snug" data-id="element-4017">
                    {item.message}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5 font-mono" data-id="element-4018">
                    {item.time}
                  </p>
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* System Health */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4019">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4020">
            <Server className="w-4 h-4" data-id="element-4021" /> Systeemgezondheid
          </h3>
          <div className="space-y-4" data-id="element-4022">
            <div className="flex items-center justify-between" data-id="element-4023">
              <span className="text-sm text-gray-600" data-id="element-4024">Strict Mode</span>
              <span className={`text-xs font-bold px-2 py-1 rounded-full ${strictModeOn ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-4025">
                {strictModeOn ? 'AAN' : 'UIT'}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-4026">
              <span className="text-sm text-gray-600" data-id="element-4027">Gem. responstijd</span>
              <span className="text-sm font-semibold text-gray-800" data-id="element-4028">
                {systemHealth.responseTime}ms
              </span>
            </div>
            <div data-id="element-4029">
              <div className="flex items-center justify-between mb-1.5" data-id="element-4030">
                <span className="text-sm text-gray-600" data-id="element-4031">Opslag</span>
                <span className="text-sm font-semibold text-gray-800" data-id="element-4032">
                  {systemHealth.storageMb} / 5 MB
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-4033">
                <div className="h-full bg-violet-500 rounded-full" style={{
                width: `${systemHealth.storagePct}%`
              }} data-id="element-4034" />
              </div>
            </div>
            <div data-id="element-4035">
              <div className="flex items-center justify-between mb-1.5" data-id="element-4036">
                <span className="text-sm text-gray-600" data-id="element-4037">Actief deze week</span>
                <span className="text-sm font-semibold text-gray-800" data-id="element-4038">
                  {stats.activeThisWeek} leerlingen
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-4039">
                <div className="h-full bg-blue-500 rounded-full" style={{
                width: `${Math.min(100, stats.activeThisWeek / Math.max(stats.totalLearners, 1) * 100)}%`
              }} data-id="element-4040" />
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Section 4: Quick Insights */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4" data-id="element-4041">
        {/* Subscriptions */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4042">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-4043">
            Abonnementen
          </h3>
          <div className="space-y-2.5" data-id="element-4044">
            {stats.planDistribution.map(item => <div key={item.label} data-id="element-4045">
                <div className="flex justify-between text-xs mb-1" data-id="element-4046">
                  <span className="text-gray-600" data-id="element-4047">{item.label}</span>
                  <span className="font-semibold text-gray-800" data-id="element-4048">
                    {item.count}
                  </span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-4049">
                  <div className={`h-full rounded-full ${item.color}`} style={{
                width: `${item.pct}%`
              }} data-id="element-4050" />
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* Top Learners */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4051">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-1.5" data-id="element-4052">
            <TrendingUp className="w-3.5 h-3.5" data-id="element-4053" /> Top Leerlingen
          </h3>
          {stats.topLearners.length > 0 ? <div className="space-y-2.5" data-id="element-4054">
              {stats.topLearners.map((item, i) => <div key={i} className="flex items-center gap-2.5" data-id="element-4055">
                  <div className="w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center flex-shrink-0" data-id="element-4056">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-4057">
                    <p className="text-xs font-medium text-gray-800 truncate" data-id="element-4058">
                      {item.name}
                    </p>
                  </div>
                  <span className="text-xs font-bold text-violet-600" data-id="element-4059">
                    {item.xp.toLocaleString('nl-NL')} XP
                  </span>
                </div>)}
            </div> : <p className="text-xs text-gray-400" data-id="element-4060">Geen leerlingen gevonden</p>}
        </motion.div>

        {/* Calibration Progress */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-4061">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-1.5" data-id="element-4062">
            <ShieldCheck className="w-3.5 h-3.5" data-id="element-4063" /> Kalibratie
          </h3>
          <div className="space-y-3" data-id="element-4064">
            <div className="flex items-end justify-between" data-id="element-4065">
              <span className="text-2xl font-bold text-gray-900" data-id="element-4066">
                {contentStats.calibration.goldStandardPct}%
              </span>
              <span className="text-xs text-gray-500" data-id="element-4067">
                {contentStats.calibration.familiesAtGoldStandard} /{' '}
                {contentStats.calibration.totalFamilies} goud
              </span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-4068">
              <motion.div initial={{
              width: 0
            }} animate={{
              width: `${contentStats.calibration.goldStandardPct}%`
            }} transition={{
              duration: 1,
              delay: 0.3
            }} className={`h-full rounded-full ${contentStats.calibration.goldStandardPct >= 80 ? 'bg-emerald-500' : contentStats.calibration.goldStandardPct >= 40 ? 'bg-amber-500' : 'bg-red-500'}`} data-id="element-4069" />
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs" data-id="element-4070">
              <div className="flex justify-between" data-id="element-4071">
                <span className="text-gray-500" data-id="element-4072">citoDifficulty</span>
                <span className="font-semibold text-gray-700" data-id="element-4073">
                  {contentStats.calibration.citoDifficultyCoveragePct}%
                </span>
              </div>
              <div className="flex justify-between" data-id="element-4074">
                <span className="text-gray-500" data-id="element-4075">solutionSteps</span>
                <span className="font-semibold text-gray-700" data-id="element-4076">
                  {contentStats.calibration.solutionStepsCoveragePct}%
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </PageLayout>;
}